import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import api from '../../services/api';
import { useAuthStore } from '../../store/authStore';
import toast from 'react-hot-toast';
import {
  UserCircleIcon,
  KeyIcon,
  CheckIcon,
  EnvelopeIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import FoundersBadge from '../../components/FoundersBadge';

export default function UserSettings() {
  const { user, company, fetchUser } = useAuthStore();
  const [activeTab, setActiveTab] = useState('profile');
  
  return (
    <div className="animate-fade-in max-w-4xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-display font-bold text-surface-900 dark:text-white">
          Account Settings
        </h1>
        <p className="text-surface-500 dark:text-surface-400 mt-1">
          Manage your personal information and security settings
        </p>
      </div>
      
      {/* Tab Navigation */}
      <div className="flex gap-1 p-1 bg-surface-100 dark:bg-surface-800 rounded-xl mb-8 w-fit">
        <button
          onClick={() => setActiveTab('profile')}
          className={`flex items-center gap-2 px-4 py-2.5 rounded-lg font-medium text-sm transition-all ${
            activeTab === 'profile'
              ? 'bg-white dark:bg-surface-700 text-surface-900 dark:text-white shadow-sm'
              : 'text-surface-600 dark:text-surface-400 hover:text-surface-900 dark:hover:text-white'
          }`}
        >
          <UserCircleIcon className="w-5 h-5" />
          Profile
        </button>
        <button
          onClick={() => setActiveTab('security')}
          className={`flex items-center gap-2 px-4 py-2.5 rounded-lg font-medium text-sm transition-all ${
            activeTab === 'security'
              ? 'bg-white dark:bg-surface-700 text-surface-900 dark:text-white shadow-sm'
              : 'text-surface-600 dark:text-surface-400 hover:text-surface-900 dark:hover:text-white'
          }`}
        >
          <KeyIcon className="w-5 h-5" />
          Security
        </button>
      </div>
      
      {/* Content */}
      {activeTab === 'profile' ? (
        <ProfileSection user={user} company={company} onUpdate={fetchUser} />
      ) : (
        <SecuritySection />
      )}
    </div>
  );
}

function ProfileSection({ user, company, onUpdate }) {
  const [formData, setFormData] = useState({
    firstName: user?.firstName || '',
    lastName: user?.lastName || ''
  });
  const [hasChanges, setHasChanges] = useState(false);
  
  const updateMutation = useMutation({
    mutationFn: (data) => api.put('/auth/profile', data),
    onSuccess: () => {
      toast.success('Profile updated successfully');
      onUpdate();
      setHasChanges(false);
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || 'Failed to update profile');
    }
  });
  
  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setHasChanges(
      value !== user?.[field] || 
      formData[field === 'firstName' ? 'lastName' : 'firstName'] !== user?.[field === 'firstName' ? 'lastName' : 'firstName']
    );
  };
  
  const handleSubmit = (e) => {
    e.preventDefault();
    updateMutation.mutate(formData);
  };
  
  const handleReset = () => {
    setFormData({
      firstName: user?.firstName || '',
      lastName: user?.lastName || ''
    });
    setHasChanges(false);
  };
  
  return (
    <div className="card p-6">
      <div className="flex items-center gap-4 mb-6 pb-6 border-b border-surface-200 dark:border-surface-700">
        <div className="relative">
          <div className="w-16 h-16 rounded-full gradient-primary flex items-center justify-center text-white text-xl font-semibold">
            {user?.firstName?.[0]}{user?.lastName?.[0]}
          </div>
          {user?.hasFoundersBadge && (
            <span className="absolute -bottom-1 -right-1 text-lg" title="Beta Founder">🏆</span>
          )}
        </div>
        <div>
          <div className="flex items-center gap-2 mb-1">
            <h2 className="text-lg font-semibold text-surface-900 dark:text-white">
              {user?.firstName} {user?.lastName}
            </h2>
            {user?.hasFoundersBadge && (
              <FoundersBadge size="medium" />
            )}
          </div>
          <p className="text-surface-500 dark:text-surface-400">
            {user?.role?.replace('_', ' ')} {company?.name ? `at ${company.name}` : ''}
          </p>
        </div>
      </div>
      
      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="label">First Name</label>
            <input
              type="text"
              value={formData.firstName}
              onChange={(e) => handleChange('firstName', e.target.value)}
              className="input"
              required
              maxLength={50}
            />
          </div>
          <div>
            <label className="label">Last Name</label>
            <input
              type="text"
              value={formData.lastName}
              onChange={(e) => handleChange('lastName', e.target.value)}
              className="input"
              required
              maxLength={50}
            />
          </div>
        </div>
        
        <div>
          <label className="label">Email Address</label>
          <div className="relative">
            <EnvelopeIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-surface-400" />
            <input
              type="email"
              value={user?.email || ''}
              disabled
              className="input pl-10 bg-surface-50 dark:bg-surface-800 text-surface-500 cursor-not-allowed"
            />
          </div>
          <p className="text-xs text-surface-500 mt-1.5">
            Email address cannot be changed. Contact support if you need to update it.
          </p>
        </div>
        
        {hasChanges && (
          <div className="flex items-center justify-end gap-3 pt-4 border-t border-surface-200 dark:border-surface-700">
            <button
              type="button"
              onClick={handleReset}
              className="btn-secondary"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={updateMutation.isPending}
              className="btn-primary"
            >
              {updateMutation.isPending ? (
                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2" />
              ) : (
                <CheckIcon className="w-5 h-5 mr-2" />
              )}
              Save Changes
            </button>
          </div>
        )}
      </form>
    </div>
  );
}

function SecuritySection() {
  const [formData, setFormData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [errors, setErrors] = useState({});
  
  const changeMutation = useMutation({
    mutationFn: (data) => api.post('/auth/change-password', data),
    onSuccess: () => {
      toast.success('Password changed successfully');
      setFormData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
      setErrors({});
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || 'Failed to change password');
    }
  });
  
  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.currentPassword) {
      newErrors.currentPassword = 'Current password is required';
    }
    
    if (!formData.newPassword) {
      newErrors.newPassword = 'New password is required';
    } else if (formData.newPassword.length < 8) {
      newErrors.newPassword = 'Password must be at least 8 characters';
    }
    
    if (!formData.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your new password';
    } else if (formData.newPassword !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  const handleSubmit = (e) => {
    e.preventDefault();
    if (validateForm()) {
      changeMutation.mutate({
        currentPassword: formData.currentPassword,
        newPassword: formData.newPassword
      });
    }
  };
  
  const getPasswordStrength = (password) => {
    if (!password) return { label: '', color: '', width: '0%' };
    
    let strength = 0;
    if (password.length >= 8) strength++;
    if (password.length >= 12) strength++;
    if (/[A-Z]/.test(password)) strength++;
    if (/[a-z]/.test(password)) strength++;
    if (/[0-9]/.test(password)) strength++;
    if (/[^A-Za-z0-9]/.test(password)) strength++;
    
    if (strength <= 2) return { label: 'Weak', color: 'bg-red-500', width: '33%' };
    if (strength <= 4) return { label: 'Medium', color: 'bg-yellow-500', width: '66%' };
    return { label: 'Strong', color: 'bg-green-500', width: '100%' };
  };
  
  const passwordStrength = getPasswordStrength(formData.newPassword);
  
  return (
    <div className="card p-6">
      <div className="flex items-center gap-3 mb-6 pb-6 border-b border-surface-200 dark:border-surface-700">
        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center">
          <KeyIcon className="w-6 h-6 text-white" />
        </div>
        <div>
          <h2 className="text-lg font-semibold text-surface-900 dark:text-white">
            Change Password
          </h2>
          <p className="text-surface-500 dark:text-surface-400 text-sm">
            Update your password to keep your account secure
          </p>
        </div>
      </div>
      
      <form onSubmit={handleSubmit} className="space-y-6 max-w-md">
        <div>
          <label className="label">Current Password</label>
          <input
            type="password"
            value={formData.currentPassword}
            onChange={(e) => setFormData({ ...formData, currentPassword: e.target.value })}
            className={`input ${errors.currentPassword ? 'border-red-500 focus:ring-red-500' : ''}`}
            placeholder="Enter your current password"
          />
          {errors.currentPassword && (
            <p className="text-sm text-red-500 mt-1 flex items-center gap-1">
              <ExclamationTriangleIcon className="w-4 h-4" />
              {errors.currentPassword}
            </p>
          )}
        </div>
        
        <div>
          <label className="label">New Password</label>
          <input
            type="password"
            value={formData.newPassword}
            onChange={(e) => setFormData({ ...formData, newPassword: e.target.value })}
            className={`input ${errors.newPassword ? 'border-red-500 focus:ring-red-500' : ''}`}
            placeholder="Enter your new password"
          />
          {formData.newPassword && (
            <div className="mt-2">
              <div className="flex items-center justify-between text-xs mb-1">
                <span className="text-surface-500">Password strength</span>
                <span className={`font-medium ${
                  passwordStrength.label === 'Weak' ? 'text-red-500' :
                  passwordStrength.label === 'Medium' ? 'text-yellow-500' : 'text-green-500'
                }`}>
                  {passwordStrength.label}
                </span>
              </div>
              <div className="h-1.5 bg-surface-200 dark:bg-surface-700 rounded-full overflow-hidden">
                <div 
                  className={`h-full ${passwordStrength.color} transition-all duration-300`}
                  style={{ width: passwordStrength.width }}
                />
              </div>
            </div>
          )}
          {errors.newPassword && (
            <p className="text-sm text-red-500 mt-1 flex items-center gap-1">
              <ExclamationTriangleIcon className="w-4 h-4" />
              {errors.newPassword}
            </p>
          )}
        </div>
        
        <div>
          <label className="label">Confirm New Password</label>
          <input
            type="password"
            value={formData.confirmPassword}
            onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
            className={`input ${errors.confirmPassword ? 'border-red-500 focus:ring-red-500' : ''}`}
            placeholder="Confirm your new password"
          />
          {errors.confirmPassword && (
            <p className="text-sm text-red-500 mt-1 flex items-center gap-1">
              <ExclamationTriangleIcon className="w-4 h-4" />
              {errors.confirmPassword}
            </p>
          )}
        </div>
        
        <div className="pt-4 border-t border-surface-200 dark:border-surface-700">
          <button
            type="submit"
            disabled={changeMutation.isPending}
            className="btn-primary"
          >
            {changeMutation.isPending ? (
              <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2" />
            ) : (
              <KeyIcon className="w-5 h-5 mr-2" />
            )}
            Change Password
          </button>
        </div>
      </form>
    </div>
  );
}

